import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { DoorOpen, List, Map as MapIcon, Minus, Orbit, Plus, Sparkles } from "lucide-react";
import { api, getJourney } from "../lib/api";
import { PageShell, PremiumNotice } from "../components/PageShell";
import { useQuantum } from "../components/QuantumTransitions";
import { formatApiError, isPremiumError } from "../context/AuthContext";

const C = { x: 500, y: 350 };
const COLORS = ["#00F0FF", "#B026FF", "#FFD700", "#FF6AD5", "#7CFFB2"];
const HORIZONS = [5, 10, 20, 30];

export const sharedChoices = (a, b) =>
  (a.choices || []).filter((c) => (b.choices || []).some((d) => d.category === c.category && d.kept === c.kept)).map((c) => c.category);

export default function Multiverse() {
  const { id } = useParams();
  const { go, withCollapse } = useQuantum();
  const [journey, setJourney] = useState(null);
  const [selves, setSelves] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("map");
  const [zoom, setZoom] = useState(1);
  const [orbit, setOrbit] = useState(false);
  const [horizon, setHorizon] = useState(10);
  const [flips, setFlips] = useState([]);
  const [ripple, setRipple] = useState(0);
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    const [j, s] = await Promise.all([getJourney(id), api.get(`/journey/${id}/selves`)]);
    setJourney(j);
    setSelves(s.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const nodes = useMemo(() => selves.map((s, i) => {
    const a = (i / Math.max(selves.length, 1)) * Math.PI * 2 - Math.PI / 2;
    const r = 170 + (s.horizon / 30) * 120;
    return { ...s, x: C.x + Math.cos(a) * r, y: C.y + Math.sin(a) * r * 0.82, color: COLORS[i % COLORS.length] };
  }), [selves]);

  const threads = useMemo(() => {
    const out = [];
    nodes.forEach((a, i) => nodes.slice(i + 1).forEach((b) => {
      const shared = sharedChoices(a, b);
      if (shared.length) out.push({ a, b, shared });
    }));
    return out;
  }, [nodes]);

  const generate = async (infinite) => {
    try {
      const { data } = await withCollapse(infinite ? "Opening the Infinite Door…" : "Collapsing possibilities into a new timeline…",
        () => api.post(`/journey/${id}/selves`, { horizon, flips: infinite ? null : flips.length ? flips : null, infinite }));
      setRipple((r) => r + 1);
      await load();
      setSelected(data.id);
      toast.success(`${data.timeline_name} has emerged.`);
    } catch (e) {
      if (isPremiumError(e)) setLocked(true); else toast.error(formatApiError(e));
    }
  };

  if (!journey) return null;
  const decisions = journey.five_decisions || [];
  const sel = nodes.find((n) => n.id === selected);

  return (
    <PageShell back={`/journey/${id}`} label="Multiverse map" wide title={<>Your <span className="text-glow-cyan">multiverse</span></>}
      subtitle="You are the bright point at the centre, the present moment. Every branch is a fictional version of you built from a different mix of your Five Decisions. Tap a branch to observe it.">
      {!decisions.length ? (
        <div className="glass rounded-3xl p-8 text-center"><p className="mb-4 text-white/70">Start by naming your Five Decisions.</p>
          <button onClick={() => go(`/decisions/${id}`)} className="btn-pill btn-pill-primary" data-testid="mv-go-decisions">The Five Decisions</button></div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <button onClick={() => setView(view === "map" ? "list" : "map")} className="btn-pill text-xs" data-testid="mv-toggle-view">
              {view === "map" ? <><List size={12} /> List view</> : <><MapIcon size={12} /> Map view</>}</button>
            {view === "map" && <>
              <button onClick={() => setZoom((z) => Math.min(z + 0.2, 1.8))} className="btn-pill text-xs" aria-label="Zoom in" data-testid="mv-zoom-in"><Plus size={12} /></button>
              <button onClick={() => setZoom((z) => Math.max(z - 0.2, 0.6))} className="btn-pill text-xs" aria-label="Zoom out" data-testid="mv-zoom-out"><Minus size={12} /></button>
              <button onClick={() => setOrbit(!orbit)} className={`btn-pill text-xs ${orbit ? "btn-pill-primary" : ""}`} data-testid="mv-orbit"><Orbit size={12} /> Possibility orbit</button>
            </>}
            <button onClick={() => go(`/decisions/${id}`)} className="btn-pill text-xs ml-auto" data-testid="mv-edit-decisions">Edit decisions</button>
          </div>

          {view === "map" ? (
            <div className="glass rounded-3xl overflow-hidden relative" data-testid="multiverse-map">
              <svg viewBox="0 0 1000 700" className="w-full h-[460px] md:h-[620px]" role="img" aria-label="Multiverse constellation of alternate selves">
                <g style={{ transform: `scale(${zoom})`, transformOrigin: "500px 350px", transition: "transform 0.4s ease" }}>
                  {ripple > 0 && <circle key={ripple} cx={C.x} cy={C.y} r="10" fill="none" stroke="#B026FF" strokeWidth="2" className="ripple" data-testid="decision-ripple" />}
                  <g className={orbit ? "orbit" : ""}>
                    {threads.map((t, i) => {
                      const on = selected && (t.a.id === selected || t.b.id === selected);
                      return <path key={i} d={`M${t.a.x},${t.a.y} Q${C.x},${C.y - 40} ${t.b.x},${t.b.y}`} fill="none" stroke="#FFD700"
                        strokeOpacity={on ? 0.8 : 0.15} strokeWidth={on ? 1.6 : 0.8} strokeDasharray="3 6" className="pathway-line" data-testid={`thread-${i}`} />;
                    })}
                    {nodes.map((n) => {
                      const dim = selected && selected !== n.id;
                      return (
                        <g key={n.id} onClick={() => setSelected(n.id)} style={{ cursor: "pointer", filter: dim ? "blur(1.5px)" : "none", opacity: !selected ? 0.55 : dim ? 0.3 : 1, transition: "opacity 0.4s, filter 0.4s" }} data-testid={`mv-node-${n.id}`}>
                          <line x1={C.x} y1={C.y} x2={n.x} y2={n.y} stroke={n.color} strokeOpacity={selected === n.id ? 0.9 : 0.35} strokeWidth={selected === n.id ? 2.5 : 1} />
                          <g className={orbit ? "orbit-counter" : ""}>
                            <circle cx={n.x} cy={n.y} r={selected === n.id ? 16 : 11} fill={n.color} style={{ filter: `drop-shadow(0 0 12px ${n.color})` }} />
                            <text x={n.x} y={n.y + 32} textAnchor="middle" fill="#fff" fontSize="15" fontFamily="Outfit">{n.timeline_name}</text>
                            <text x={n.x} y={n.y + 50} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="12">{`+${n.horizon} yrs · age ${n.age}`}</text>
                          </g>
                        </g>
                      );
                    })}
                    <g onClick={() => generate(true)} style={{ cursor: "pointer" }} data-testid="infinite-door">
                      <rect x="880" y="590" width="56" height="84" rx="28" fill="rgba(255,215,0,0.06)" stroke="#FFD700" strokeOpacity="0.7" />
                      <text x="908" y="690" textAnchor="middle" fill="#FFD700" fontSize="11">Infinite Door</text>
                    </g>
                  </g>
                  <circle cx={C.x} cy={C.y} r="22" fill="#fff" opacity="0.5" className="heartbeat" />
                  <circle cx={C.x} cy={C.y} r="14" fill="#fff" style={{ filter: "drop-shadow(0 0 18px #fff)" }} data-testid="present-node" />
                  <text x={C.x} y={C.y + 40} textAnchor="middle" fill="#fff" fontSize="13" fontFamily="Outfit">You · now</text>
                </g>
              </svg>
              {!nodes.length && <p className="absolute inset-x-0 bottom-6 text-center text-sm text-white/45">No timelines observed yet. Generate your first alternate self below.</p>}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="multiverse-list">
              {nodes.map((n) => <RealityCard key={n.id} s={n} onEnter={() => go(`/self/${n.id}`)} />)}
              {!nodes.length && <p className="text-sm text-white/45">No timelines yet.</p>}
            </div>
          )}

          <AnimatePresence>
            {sel && view === "map" && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-5">
                <RealityCard s={sel} onEnter={() => go(`/self/${sel.id}`)}
                  shared={threads.filter((t) => t.a.id === sel.id || t.b.id === sel.id).map((t) => ({ with: (t.a.id === sel.id ? t.b : t.a).timeline_name, cats: t.shared }))} />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="glass rounded-3xl p-6 mt-6" data-testid="mv-generator">
            <div className="font-mono-label mb-3">Observe a new timeline</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {HORIZONS.map((h) => <button key={h} onClick={() => setHorizon(h)} data-testid={`mv-horizon-${h}`}
                className={`btn-pill text-xs ${horizon === h ? "btn-pill-primary" : ""}`}>{h} years ahead</button>)}
            </div>
            <div className="text-xs text-white/50 mb-2">Choose which decisions this version of you made differently (optional):</div>
            <div className="flex flex-wrap gap-2 mb-5">
              {decisions.map((d, i) => (
                <button key={i} onClick={() => setFlips(flips.includes(i) ? flips.filter((f) => f !== i) : [...flips, i])} data-testid={`mv-flip-${i}`}
                  className="text-[11px] px-3 py-1.5 rounded-full border transition-colors"
                  style={{ borderColor: flips.includes(i) ? "#B026FF" : "rgba(255,255,255,0.1)", color: flips.includes(i) ? "#d9a6ff" : "rgba(255,255,255,0.55)" }}>
                  {flips.includes(i) ? "Flipped: " : ""}{d.text.slice(0, 40)}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => generate(false)} className="btn-pill btn-pill-primary" data-testid="mv-generate"><Sparkles size={14} /> Generate alternate self</button>
              <button onClick={() => generate(true)} className="btn-pill btn-pill-gold" data-testid="mv-infinite"><DoorOpen size={14} /> Step through the Infinite Door</button>
            </div>
          </div>
          {locked && <div className="mt-6"><PremiumNotice feature="Meeting more alternate selves" /></div>}
          <p className="text-[11px] text-white/35 mt-6">Superposition, entanglement and the observer effect are used here as artistic metaphors. They don't depict real quantum behaviour, and these timelines are fiction, not predictions.</p>
        </>
      )}
    </PageShell>
  );
}

export const RealityCard = ({ s, onEnter, shared }) => (
  <div className="glass rounded-2xl p-6 float-y" data-testid={`reality-card-${s.id}`} style={{ borderColor: `${s.color || "#00F0FF"}55` }}>
    <div className="font-mono-label mb-1" style={{ color: s.color || "#00F0FF" }}>{s.emotional_theme} · age {s.age} · +{s.horizon} yrs</div>
    <h3 className="text-2xl font-display mb-2">{s.timeline_name}</h3>
    <p className="text-sm text-white/65 mb-3">{s.lifestyle}</p>
    <div className="text-xs text-white/55 mb-3"><span className="font-mono-label mr-2">Finances</span>{s.finances}</div>
    <div className="flex flex-wrap gap-1.5 mb-4">
      {(s.choices || []).map((c) => <span key={c.category} className="text-[10px] px-2 py-1 rounded-full border border-white/10 text-white/60">{c.kept ? "Kept" : "Changed"}: {c.chosen?.slice(0, 34)}</span>)}
    </div>
    {shared?.length > 0 && <div className="text-[11px] text-amber-200/80 mb-4" data-testid="entangled-info">
      Entangled with {shared.map((x) => `${x.with} (${x.cats.join(", ")})`).join("; ")}</div>}
    <button onClick={onEnter} className="btn-pill btn-pill-primary text-xs" data-testid={`enter-self-${s.id}`}>Enter this timeline</button>
  </div>
);
